import { brand, neutral, semantic } from "@/constants/colors";
import { fonts } from "@/constants/fonts";
import type React from "react";
import type { ReactNode } from "react";
import {
  Pressable,
  StyleSheet,
  type TextStyle,
  useColorScheme,
  View,
  type ViewStyle,
} from "react-native";
import ThemedText from "../core/ThemedText";

type ButtonGroupVariant = "filled" | "outline" | "ghost";
type ButtonGroupSize = "sm" | "md" | "lg";
type ButtonGroupColor = "primary" | "success" | "warning" | "error" | "neutral";
type ButtonGroupOrientation = "horizontal" | "vertical";

interface ButtonGroupItem {
  label: string;
  value: string;
  icon?: ReactNode;
  disabled?: boolean;
}

interface ButtonGroupProps {
  items: ButtonGroupItem[];
  selectedValue?: string;
  onValueChange?: (value: string) => void;
  variant?: ButtonGroupVariant;
  size?: ButtonGroupSize;
  color?: ButtonGroupColor;
  orientation?: ButtonGroupOrientation;
  disabled?: boolean;
  style?: ViewStyle;
  fullWidth?: boolean;
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  items,
  selectedValue,
  onValueChange,
  variant = "outline",
  size = "md",
  color = "primary",
  orientation = "horizontal",
  disabled = false,
  style,
  fullWidth = false,
}) => {
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";

  const sizeStyles: Record<
    ButtonGroupSize,
    { height: number; fontSize: number; padding: number }
  > = {
    sm: { height: 36, fontSize: 14, padding: 12 },
    md: { height: 44, fontSize: 16, padding: 16 },
    lg: { height: 52, fontSize: 18, padding: 20 },
  };

  const getButtonColor = () => {
    switch (color) {
      case "primary":
        return isDark ? brand.primary.dark : brand.primary.main;
      case "success":
        return isDark ? semantic.success.dark : semantic.success.main;
      case "warning":
        return semantic.warning.main;
      case "error":
        return isDark ? semantic.error.dark : semantic.error.main;
      case "neutral":
        return isDark ? neutral[600] : neutral[300];
    }
  };

  const isLightColor = (hexColor: string) => {
    const r = Number.parseInt(hexColor.slice(1, 3), 16);
    const g = Number.parseInt(hexColor.slice(3, 5), 16);
    const b = Number.parseInt(hexColor.slice(5, 7), 16);

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
  };

  const getItemStyle = (item: ButtonGroupItem, index: number): ViewStyle => {
    const isSelected = selectedValue === item.value;
    const buttonColor = getButtonColor();
    const isFirst = index === 0;
    const isLast = index === items.length - 1;

    const baseStyle: ViewStyle = {
      height: sizeStyles[size].height,
      paddingHorizontal: sizeStyles[size].padding,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      borderColor: buttonColor,
    };

    if (fullWidth && orientation === "horizontal") {
      baseStyle.flex = 1;
    }

    // Border radius for first and last items
    if (orientation === "horizontal") {
      if (isFirst) {
        baseStyle.borderTopLeftRadius = 12;
        baseStyle.borderBottomLeftRadius = 12;
      }
      if (isLast) {
        baseStyle.borderTopRightRadius = 12;
        baseStyle.borderBottomRightRadius = 12;
      }
    } else {
      if (isFirst) {
        baseStyle.borderTopLeftRadius = 12;
        baseStyle.borderTopRightRadius = 12;
      }
      if (isLast) {
        baseStyle.borderBottomLeftRadius = 12;
        baseStyle.borderBottomRightRadius = 12;
      }
    }

    // Variant specific styles
    switch (variant) {
      case "filled":
        return {
          ...baseStyle,
          backgroundColor: isSelected ? buttonColor : "transparent",
          borderWidth: 1,
        };
      case "outline":
        return {
          ...baseStyle,
          backgroundColor: isSelected ? buttonColor + "20" : "transparent",
          borderWidth: 1,
        };
      case "ghost":
        return {
          ...baseStyle,
          backgroundColor: isSelected ? buttonColor + "15" : "transparent",
          borderWidth: 0,
        };
    }
  };

  const getTextColor = (item: ButtonGroupItem): string => {
    const isSelected = selectedValue === item.value;
    const isDisabled = disabled || item.disabled;

    if (isDisabled) return isDark ? neutral[500] : neutral[400];

    const buttonColor = getButtonColor();

    switch (variant) {
      case "filled":
        if (isSelected) {
          if (color === "primary") {
            return "#FFFFFF";
          }
          return isLightColor(buttonColor) ? neutral[900] : "#FFFFFF";
        }
        return isDark ? "#FFFFFF" : "#000000";
      case "outline":
      case "ghost":
        return isDark ? "#FFFFFF" : "#000000";
    }
  };

  const getContainerStyle = (): ViewStyle => {
    const buttonColor = getButtonColor();
    const baseStyle: ViewStyle = {
      borderRadius: 12,
      overflow: "hidden",
    };

    if (orientation === "horizontal") {
      baseStyle.flexDirection = "row";
    } else {
      baseStyle.flexDirection = "column";
    }

    if (variant === "outline" || variant === "filled") {
      // Outer border for the group
      baseStyle.borderWidth = 1;
      baseStyle.borderColor = buttonColor;
    }

    if (fullWidth) {
      baseStyle.width = "100%";
    }

    return baseStyle;
  };

  const getSeparatorStyle = (): ViewStyle => {
    const buttonColor = getButtonColor();
    if (variant === "ghost") return { width: 0, height: 0 };

    if (orientation === "horizontal") {
      return {
        width: 1,
        backgroundColor: buttonColor,
      };
    }
    return {
      height: 1,
      backgroundColor: buttonColor,
    };
  };

  return (
    <View style={[getContainerStyle(), style]}>
      {items.map((item, index) => (
        <View key={item.value} style={{ flexDirection: orientation === "horizontal" ? "row" : "column" }}>
          {index > 0 && <View style={getSeparatorStyle()} />}
          <Pressable
            onPress={() => onValueChange?.(item.value)}
            disabled={disabled || item.disabled}
            style={({ pressed }) => [
              getItemStyle(item, index),
              {
                opacity: disabled || item.disabled ? 0.5 : pressed ? 0.7 : 1,
              },
            ]}
          >
            {item.icon && (
              <View style={styles.iconContainer}>{item.icon}</View>
            )}
            <ThemedText
              style={{
                fontSize: sizeStyles[size].fontSize,
                color: getTextColor(item),
                fontFamily: fonts.semiBold,
              }}
            >
              {item.label}
            </ThemedText>
          </Pressable>
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  iconContainer: {
    marginRight: 8,
  },
});

export default ButtonGroup;
